/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { modifier } from 'ember-modifier';
import { type Registry as Services, service } from '@ember/service';
// eslint-disable-next-line import-x/no-named-as-default
import type Litepicker from 'litepicker';
import { waitFor } from '@ember/test-waiters';
import { addMinutes } from 'date-fns/addMinutes';
import { type IDropdown } from 'ember-bootstrap/components/bs-dropdown';
import {
  utcDate,
  isDateValid,
  APP_MIN_DATE,
  APP_MAX_DATE,
} from 'uplisting-frontend/utils/date';
import {
  LITEPICKER_BUTTON_TEXT,
  loadLitepicker,
} from 'uplisting-frontend/utils/litepicker';

// Litepicker reads the Date's local Y/M/D for what it draws. For inputs that
// are already at local midnight in any zone, or at UTC midnight in east-of-UTC
// zones (where UTC midnight falls in the same local day), no shift is needed.
// Only west-of-UTC viewers need a forward shift to drag a UTC-midnight Date
// out of the previous local evening and into the intended local day.
function asDisplayDate(date: Date): Date {
  const offset = date.getTimezoneOffset();
  return offset > 0 ? addMinutes(date, offset) : date;
}

interface IArgs {
  startDate: Date | string | undefined;
  endDate?: Date | string | undefined;
  singleMode?: false;
  minDate?: Date;
  maxDate?: Date;
  inLocalTimeZone?: true;
  showTooltip?: false;
  toggleLabel?: string;

  onDateSelect(date1: Date, date2?: Date): void;
  // Hand the parent an imperative handle to open the picker, so it can drive
  // the flow (e.g. open the end picker after a start date is chosen) without
  // reaching into this component's DOM.
  onReady?(api: { open(): void }): void;
}

interface PickerDateRangeSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiPickerDateComponent extends Component<PickerDateRangeSignature> {
  @service intl!: Services['intl'];

  @tracked picker!: Litepicker;

  @tracked dropdown?: IDropdown;
  @tracked selectingMonth = false;

  @tracked _overriddenDate?: Date;

  private headerElement?: Element;
  private headerClickListener?: EventListener;

  registerApi = modifier((_element: HTMLElement, [dd]: [IDropdown]) => {
    this.args.onReady?.({ open: () => dd.openDropdown() });
  });

  @cached
  get maxDate(): Date {
    return this.args.maxDate ?? APP_MAX_DATE;
  }

  @cached
  get maxDateYear(): number {
    return this.maxDate.getFullYear();
  }

  @cached
  get minDate(): Date {
    return this.args.minDate ?? APP_MIN_DATE;
  }

  @cached
  get minDateYear(): number {
    return this.minDate.getFullYear();
  }

  @cached
  get startDate(): Date | string | undefined {
    const { startDate } = this.args;

    if (isDateValid(startDate)) {
      return asDisplayDate(startDate as Date);
    }

    return startDate;
  }

  @cached
  get endDate(): Date | string | undefined {
    const { endDate } = this.args;

    if (isDateValid(endDate)) {
      return asDisplayDate(endDate as Date);
    }

    return endDate;
  }

  @cached
  get singleMode(): boolean {
    return this.args.singleMode ?? true;
  }

  @cached
  get inLocalTimeZone(): boolean {
    return this.args.inLocalTimeZone ?? false;
  }

  @cached
  get overriddenDate(): Date {
    return this._overriddenDate || (this.startDate as Date) || new Date();
  }

  @cached
  get currentOverriddenYear(): number {
    return this.overriddenDate.getFullYear();
  }

  @cached
  get previousYearDisabled(): boolean {
    return this.currentOverriddenYear <= this.minDateYear;
  }

  @cached
  get nextYearDisabled(): boolean {
    return this.currentOverriddenYear >= this.maxDateYear;
  }

  @cached
  get disabledMonths(): number[] {
    const { minDateYear, maxDateYear, currentOverriddenYear } = this;
    const disabledMonths: number[] = [];

    if (currentOverriddenYear === minDateYear) {
      const minMonth = this.minDate.getMonth();

      for (let month = 0; month < minMonth; month++) {
        disabledMonths.push(month);
      }
    } else if (currentOverriddenYear === maxDateYear) {
      const maxMonth = this.maxDate.getMonth();

      for (let month = maxMonth + 1; month < 12; month++) {
        disabledMonths.push(month);
      }
    } else if (this.previousYearDisabled || this.nextYearDisabled) {
      for (let month = 0; month < 12; month++) {
        disabledMonths.push(month);
      }
    }

    return disabledMonths;
  }

  @action
  @waitFor
  async handleSetupPicker(
    dd: IDropdown,
    element: HTMLDivElement,
  ): Promise<void> {
    this.dropdown = dd;

    const LitepickerClass = await loadLitepicker();

    this.picker = new LitepickerClass({
      element,
      startDate: this.startDate,
      endDate: this.endDate,
      parentEl: element,
      singleMode: this.singleMode,
      minDate: this.minDate,
      maxDate: this.maxDate,
      lang: this.intl.primaryLocale,
      position: 'top left',
      showTooltip: this.args.showTooltip ?? true,
      buttonText: LITEPICKER_BUTTON_TEXT,
    });

    this.picker.on('selected', (value1, value2) => {
      const date1 = this.inLocalTimeZone
        ? value1.dateInstance
        : utcDate(value1.dateInstance);

      const params: [Date] | [Date, Date] = [date1];

      if (value2) {
        const date2 = this.inLocalTimeZone
          ? value2.dateInstance
          : utcDate(value2.dateInstance);

        params.push(date2);
      }

      this.args.onDateSelect(...params);

      this.handleWillDestroy();
    });

    this.picker.show();

    this.#setupClickableHeader();

    this.picker.on('change:month', () => {
      this.#setupClickableHeader();
    });
  }

  @action
  handleWillDestroy(): void {
    if (this.headerElement && this.headerClickListener) {
      this.headerElement.removeEventListener(
        'click',
        this.headerClickListener,
        false,
      );

      this.headerElement = undefined;
      this.headerClickListener = undefined;
    }

    if (this.picker) {
      this.picker.hide();
      this.picker.destroy();
    }

    this.dropdown?.closeDropdown();
    this.dropdown = undefined;
    this.selectingMonth = false;
  }

  @action
  handlePreviousPeriod(): void {
    const date = this.overriddenDate;

    this._overriddenDate = new Date(date.setFullYear(date.getFullYear() - 1));
  }

  @action
  handleNextPeriod(): void {
    const date = this.overriddenDate;

    this._overriddenDate = new Date(date.setFullYear(date.getFullYear() + 1));
  }

  @action
  handleMonthSelect(month: number): void {
    if (this.disabledMonths.includes(month)) {
      return;
    }

    const date = new Date(this.startDate ?? new Date());

    date.setMonth(month);
    date.setFullYear(this.currentOverriddenYear);

    this.picker.setOptions({
      startDate: date,
    });

    this.selectingMonth = false;

    this.#setupClickableHeader();
  }

  #setupClickableHeader(): void {
    if (this.headerElement && this.headerClickListener) {
      this.headerElement.removeEventListener('click', this.headerClickListener);
    }

    const selector = this.selectingMonth
      ? '#custom-view-header div'
      : '.month-item-header div';

    const header = window.document.querySelector(selector);

    if (!header) {
      return;
    }

    this.headerElement = header;
    this.headerClickListener = () => {
      // @ts-expect-error - ignoring accessing private property error
      this._overriddenDate = this.picker.calendars?.[0]?.dateInstance;
      this.selectingMonth = true;
    };

    this.headerElement.addEventListener(
      'click',
      this.headerClickListener,
      false,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Picker::Date': typeof UiPickerDateComponent;
  }
}
